import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Upload, Download, Trash2, Edit2, Save, X, FileText, Image as ImageIcon, ArrowUpDown, Search, Paperclip } from 'lucide-react';
import FilePickerDialog from './FilePickerDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SortButton = ({ field, children, sortField, sortOrder, handleSort }) => (
  <button
    onClick={() => handleSort(field)}
    className="flex items-center gap-1 hover:text-indigo-600 transition-colors"
  >
    {children}
    <ArrowUpDown className="w-3 h-3" />
    {sortField === field && (
      <span className="text-xs ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
    )}
  </button>
);

const CompletedOrders = ({ user, companyFilter = 'all' }) => {
  const [completedOrders, setCompletedOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [uploadingFile, setUploadingFile] = useState(null);
  const [pickerOrderId, setPickerOrderId] = useState(null);
  const [sortField, setSortField] = useState('completed_at');
  const [sortOrder, setSortOrder] = useState('desc'); // desc = newest first
  const [searchTerm, setSearchTerm] = useState('');

  const fetchCompletedOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/orders/completed`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setCompletedOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch completed orders');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const filteredAndSortedOrders = useMemo(() => {
    // First filter by search term
    let filtered = completedOrders;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = completedOrders.filter(order =>
        order.product_name?.toLowerCase().includes(search) ||
        order.order_given_to?.toLowerCase().includes(search) ||
        order.batch_no?.toLowerCase().includes(search) ||
        order.notes?.toLowerCase().includes(search)
      );
    }

    // Filter by company
    if (companyFilter && companyFilter !== 'all') {
      filtered = filtered.filter(order => 
        order.manufactured_by?.toUpperCase() === companyFilter.toUpperCase()
      );
    }

    // Then sort
    return [...filtered].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle different data types
      if (sortField === 'order_qty') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortField === 'completed_at' || sortField === 'order_received_date') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }, [completedOrders, sortField, sortOrder, searchTerm]);

  useEffect(() => {
    fetchCompletedOrders();
  }, []);

  const handleEdit = (order) => {
    setEditingId(order.id);
    setEditData({
      batch_no: order.batch_no || '',
      expiry_date: order.expiry_date || '',
      notes: order.notes || ''
    });
  };

  const handleSave = async (orderId) => {
    try {
      await axios.put(`${API}/orders/completed/${orderId}`, editData, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Order updated successfully');
      setEditingId(null);
      fetchCompletedOrders();
    } catch (error) {
      console.error('Save error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to update order';
      toast.error(errorMsg);
    }
  };

  const handleFileUpload = async (orderId, file) => {
    console.log('Uploading file:', file.name, 'for order:', orderId);
    setUploadingFile(orderId);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/orders/completed/${orderId}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${user.token}`
        }
      });
      console.log('Upload response:', response.data);
      toast.success(`File "${file.name}" uploaded & added to Documents/Media Library`);
      fetchCompletedOrders();
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to upload file';
      toast.error(errorMsg);
    } finally {
      setUploadingFile(null);
    }
  };

  // Picker callback: either upload new OR attach from library
  const handlePickerResult = async ({ source, file, libraryItem, libraryKind }) => {
    const orderId = pickerOrderId;
    if (!orderId) return;
    if (source === 'upload' && file) {
      await handleFileUpload(orderId, file);
    } else if (source === 'library' && libraryItem) {
      setUploadingFile(orderId);
      try {
        await axios.post(
          `${API}/orders/completed/${orderId}/attachments/from-library`,
          { library_id: libraryItem.id, source: libraryKind },
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
        toast.success(`Attached "${libraryItem.original_name}" from library`);
        fetchCompletedOrders();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to attach from library');
      } finally {
        setUploadingFile(null);
      }
    }
  };

  const handleDownloadAttachment = async (orderId, attachment, index) => {
    try {
      // Handle both old format (string) and new format (object)
      const fileId = typeof attachment === 'string' ? attachment : attachment.file_id;
      const filename = typeof attachment === 'object' && attachment.filename 
        ? attachment.filename 
        : `attachment_${index + 1}`;
      
      const response = await axios.get(`${API}/orders/completed/${orderId}/attachments/${fileId}`, {
        headers: { 'Authorization': `Bearer ${user.token}` },
        responseType: 'blob'
      });
      
      // Get content-type from response headers
      const contentType = response.headers['content-type'];
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Clean up the URL object
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
      
      toast.success(`Downloaded: ${filename}`);
    } catch (error) {
      console.error('Download error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to download file';
      toast.error(errorMsg);
    }
  };

  const handleDeleteAttachment = async (orderId, fileId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await axios.delete(`${API}/orders/completed/${orderId}/attachments/${fileId}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Attachment deleted');
      fetchCompletedOrders();
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const handleDeleteOrder = async (orderId, productName) => {
    if (!window.confirm(`Are you sure you want to delete the completed order for "${productName}"? This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/orders/completed/${orderId}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Completed order deleted successfully');
      fetchCompletedOrders();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete order';
      toast.error(errorMsg);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-slate-800">Completed Orders</h3>
        <div className="flex items-center gap-4">
          <Input
            icon={<Search className="w-4 h-4" />}
            type="text"
            placeholder="Search completed orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-64"
          />
          <p className="text-sm text-slate-600 whitespace-nowrap">
            Sorted by: {sortField === 'completed_at' ? 'Recently Completed' : sortField.replace(/_/g, ' ')} 
            ({sortOrder === 'asc' ? 'Low to High' : 'High to Low'})
          </p>
        </div>
      </div>
      
      {filteredAndSortedOrders.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          No completed orders yet. Orders will appear here when received date is added.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                    <SortButton field="product_name" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort}>Product</SortButton>
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Order Given To</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                    <SortButton field="order_qty" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort}>Qty</SortButton>
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                    <SortButton field="order_given_date" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort}>Given Date</SortButton>
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                    <SortButton field="order_received_date" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort}>Received Date</SortButton>
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Batch No.</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Expiry Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Notes</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Attachments</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedOrders.map((order) => (
                  <tr key={order.id} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-800">{order.product_name}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{order.order_given_to}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{order.order_qty}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{order.order_given_date}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{order.order_received_date}</td>
                    <td className="px-4 py-3 text-sm">
                      {editingId === order.id ? (
                        <input
                          type="text"
                          value={editData.batch_no}
                          onChange={(e) => setEditData({ ...editData, batch_no: e.target.value })}
                          className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                        />
                      ) : (
                        <span className="text-slate-700">{order.batch_no || '-'}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {editingId === order.id ? (
                        <input
                          type="date"
                          value={editData.expiry_date}
                          onChange={(e) => setEditData({ ...editData, expiry_date: e.target.value })}
                          className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                        />
                      ) : (
                        <span className="text-slate-700">{order.expiry_date || '-'}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {editingId === order.id ? (
                        <input
                          type="text"
                          value={editData.notes}
                          onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                          className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                        />
                      ) : (
                        <span className="text-slate-600">{order.notes || '-'}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-2">
                        {order.attachments && order.attachments.length > 0 ? (
                          order.attachments.map((attachment, idx) => {
                            // Handle both old format (string) and new format (object)
                            const fileId = typeof attachment === 'string' ? attachment : attachment.file_id;
                            const filename = typeof attachment === 'object' && attachment.filename 
                              ? attachment.filename 
                              : `File ${idx + 1}`;
                            const fileSize = typeof attachment === 'object' && attachment.file_size
                              ? `(${(attachment.file_size / 1024).toFixed(1)}KB)`
                              : '';
                            
                            return (
                              <div key={fileId} className="flex items-center gap-2">
                                <Button
                                  onClick={() => handleDownloadAttachment(order.id, attachment, idx)}
                                  size="sm"
                                  variant="ghost"
                                  className="text-xs"
                                  title={filename}
                                >
                                  <Download className="w-3 h-3 mr-1" />
                                  {filename.length > 15 ? filename.substring(0, 15) + '...' : filename}
                                  {fileSize && <span className="ml-1 text-slate-400">{fileSize}</span>}
                                </Button>
                                <Button
                                  onClick={() => handleDeleteAttachment(order.id, fileId)}
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600 text-xs"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            );
                          })
                        ) : (
                          <span className="text-xs text-slate-500">No files</span>
                        )}
                        <div>
                          <button
                            type="button"
                            onClick={() => setPickerOrderId(order.id)}
                            disabled={uploadingFile === order.id}
                            data-testid={`add-file-btn-${order.id}`}
                            className={`inline-flex items-center justify-center px-3 py-2 text-xs font-medium rounded-md border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer transition-colors ${
                              uploadingFile === order.id ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          >
                            <Paperclip className="w-3 h-3 mr-1" />
                            {uploadingFile === order.id ? 'Saving...' : 'Add File'}
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {editingId === order.id ? (
                        <div className="flex gap-2">
                          <Button onClick={() => handleSave(order.id)} size="sm" variant="ghost">
                            <Save className="w-4 h-4" />
                          </Button>
                          <Button onClick={() => setEditingId(null)} size="sm" variant="ghost">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button onClick={() => handleEdit(order)} size="sm" variant="ghost" title="Edit">
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          {(user.role === 'admin' || user.role === 'winlife') && (
                            <Button 
                              onClick={() => handleDeleteOrder(order.id, order.product_name)} 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-600 hover:text-red-800 hover:bg-red-50"
                              title="Delete Order"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Smart File Picker — Upload New OR Pick from Library */}
      <FilePickerDialog
        open={!!pickerOrderId}
        onClose={() => setPickerOrderId(null)}
        user={user}
        kind="any"
        title="Attach to Completed Order"
        onPick={handlePickerResult}
      />
    </div>
  );
};

export default CompletedOrders;
